/**
 * Form Survey Publik
 * Halaman publik untuk mengisi survey kepuasan masyarakat
 * Dapat diakses tanpa login dengan link: DOMAIN_ESA_OFFICE/publik/survey-kepuasan/:id
 * Menggunakan slider untuk navigasi step-by-step
 */

import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import CircularProgress from "@mui/material/CircularProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Fade from "@mui/material/Fade";
import Autocomplete from "@mui/material/Autocomplete";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";

// Images
import esaOfficeBanner from "assets/images/eo_cover.png";
import eo_mini1 from "assets/images/eo_mini1.png";

// Utils
import { API_ESA_OFFICE } from "utils";
import Icon from "@mui/material/Icon";

function FormSurveyPublik() {
  // Survey id dari URL parameter
  const { id } = useParams();
  const surveyId = id || 1; // Default id = 1 jika tidak ada parameter

  // Query parameter untuk layanan
  const [searchParams] = useSearchParams();
  const layananId = searchParams.get("layanan");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyDetail, setSurveyDetail] = useState(null);
  const [layananDetail, setLayananDetail] = useState(null);
  const [dinasDetail, setDinasDetail] = useState(null);
  const [pertanyaan, setPertanyaan] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // Mulai langsung di pertanyaan pertama (step 0)
  const [formData, setFormData] = useState({
    responden: {
      nama: "",
      pendidikan: "",
      pekerjaan: "",
      id_smart: null,
      ip_address: null,
      id_layanan: null,
    },
    jawaban: {},
  });

  // State untuk deteksi mobile
  const [isMobile, setIsMobile] = useState(false);

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Pilih banner berdasarkan ukuran layar
  const bannerImage = isMobile ? eo_mini1 : esaOfficeBanner;

  // Fungsi untuk mendapatkan IP address
  const fetchIPAddress = async () => {
    try {
      // Prioritas 1: Coba backend endpoint sendiri terlebih dahulu
      try {
        const backendResponse = await axios.get(
          `${API_ESA_OFFICE}survey-kepuasan/client-ip`,
          {
            timeout: 5000,
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (backendResponse.data?.ip_address) {
          const ipAddress = backendResponse.data.ip_address?.trim();
          // Accept all IP addresses including localhost (::1, 127.0.0.1)
          if (
            ipAddress &&
            typeof ipAddress === "string" &&
            (ipAddress === "::1" ||
              ipAddress === "127.0.0.1" ||
              ipAddress === "localhost" ||
              /^(\d{1,3}\.){3}\d{1,3}$/.test(ipAddress) ||
              /^([0-9a-fA-F]{0,4}:){1,7}[0-9a-fA-F]{0,4}$/.test(ipAddress))
          ) {
            setFormData((prev) => ({
              ...prev,
              responden: {
                ...prev.responden,
                ip_address: ipAddress,
              },
            }));
            console.log(
              "✅ IP address berhasil diambil dari backend:",
              ipAddress
            );
            return ipAddress;
          }
        }
      } catch (backendError) {
        console.warn(
          "⚠️ Backend IP endpoint gagal, mencoba API eksternal...",
          backendError.message
        );
      }

      // Prioritas 2: API eksternal sebagai fallback
      const externalApis = [
        {
          url: "https://api.ipify.org?format=json",
          extract: (data) => data?.ip,
        },
        {
          url: "https://api64.ipify.org?format=json",
          extract: (data) => data?.ip,
        },
        {
          url: "https://api.ipify.org",
          extract: (data) => {
            if (typeof data === "string") {
              return data.trim();
            }
            return data?.ip;
          },
        },
        {
          url: "https://api.ip.sb/ip",
          extract: (data) => {
            if (typeof data === "string") {
              return data.trim();
            }
            return data?.ip;
          },
        },
      ];

      for (const api of externalApis) {
        try {
          const response = await axios.get(api.url, {
            timeout: 5000,
            headers: {
              Accept: api.url.includes("ipify")
                ? "application/json"
                : "text/plain",
            },
            transformResponse: [
              (data) => {
                try {
                  return JSON.parse(data);
                } catch {
                  return data;
                }
              },
            ],
          });

          let ipAddress = null;

          // Extract IP using the provided function
          if (typeof api.extract === "function") {
            ipAddress = api.extract(response.data);
          } else if (response.data?.ip) {
            ipAddress = response.data.ip;
          } else if (typeof response.data === "string") {
            ipAddress = response.data.trim();
          }

          // Validate IP address format (accept all including localhost)
          if (
            ipAddress &&
            typeof ipAddress === "string" &&
            // Accept both IPv4 and IPv6 (including localhost)
            (/^(\d{1,3}\.){3}\d{1,3}$/.test(ipAddress) ||
              /^([0-9a-fA-F]{0,4}:){1,7}[0-9a-fA-F]{0,4}$/.test(ipAddress) ||
              ipAddress === "::1" ||
              ipAddress === "127.0.0.1" ||
              ipAddress === "localhost")
          ) {
            // Update state
            setFormData((prev) => ({
              ...prev,
              responden: {
                ...prev.responden,
                ip_address: ipAddress,
              },
            }));
            console.log(
              "✅ IP address berhasil diambil dari API eksternal:",
              ipAddress
            );
            return ipAddress;
          } else {
            console.warn(`⚠️ Invalid IP format from ${api.url}:`, ipAddress);
          }
        } catch (err) {
          // Log error but continue to next API
          if (err.code === "ECONNABORTED") {
            console.warn(`⏱️ Timeout fetching IP from ${api.url}`);
          } else if (err.response) {
            console.warn(
              `❌ Error ${err.response.status} from ${api.url}:`,
              err.response.statusText
            );
          } else {
            console.warn(`❌ Failed to fetch IP from ${api.url}:`, err.message);
          }
          continue;
        }
      }

      console.warn("⚠️ Could not fetch IP address from any source");
      // Return null jika semua gagal, backend akan handle IP saat submit
      return null;
    } catch (error) {
      console.error("❌ Error fetching IP address:", error);
      // Return null jika semua gagal, backend akan handle IP saat submit
      return null;
    }
  };

  useEffect(() => {
    fetchSurveyDetail(surveyId);
    // Ambil IP address saat component mount
    fetchIPAddress();

    // Ambil data layanan jika ada layananId dari query parameter
    if (layananId) {
      fetchLayananDetail(layananId);
    }
  }, [surveyId, layananId]);

  const fetchLayananDetail = async (layananId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/layanan-publik/${layananId}`
      );
      setLayananDetail(response.data);
      if (response.data.nama_dinas) {
        setDinasDetail({ nama_dinas: response.data.nama_dinas });
      }

      // Update id_layanan di formData
      setFormData((prev) => ({
        ...prev,
        responden: {
          ...prev.responden,
          id_layanan: parseInt(layananId),
        },
      }));
    } catch (error) {
      console.error("Error fetching layanan detail:", error);
      // Tidak perlu show error karena layanan opsional
    }
  };

  const fetchSurveyDetail = async (surveyId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/survey/${surveyId}/detail`
      );
      setSurveyDetail(response.data.survey);

      // Sort pertanyaan by id untuk konsistensi
      const sortedPertanyaan = (response.data.pertanyaan || []).sort(
        (a, b) => a.id - b.id
      );
      setPertanyaan(sortedPertanyaan);

      // Initialize jawaban object
      const initialJawaban = {};
      sortedPertanyaan.forEach((p) => {
        if (p.tipe === "nilai") {
          initialJawaban[p.id] = null;
        } else if (p.tipe === "teks") {
          initialJawaban[p.id] = "";
        }
      });

      // Gunakan layananId dari query parameter saja
      const finalLayananId = layananId ? parseInt(layananId) : null;

      // Cek localStorage untuk user.uid_RSS
      const user = JSON.parse(localStorage.getItem("user"));
      const idSmart = user && user.uid_RSS ? user.uid_RSS : null;

      setFormData((prev) => ({
        ...prev,
        jawaban: initialJawaban,
        responden: {
          nama: "",
          pendidikan: "",
          pekerjaan: "",
          id_smart: idSmart, // Ambil dari localStorage jika ada
          ip_address: prev.responden.ip_address, // Pertahankan IP address
          id_layanan: finalLayananId,
        },
      }));
    } catch (error) {
      console.error("Error fetching survey detail:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Gagal memuat data survey. Survey tidak ditemukan.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRespondenChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      responden: {
        ...prev.responden,
        [field]: value,
      },
    }));
  };

  const handleJawabanChange = (pertanyaanId, value) => {
    setFormData((prev) => ({
      ...prev,
      jawaban: {
        ...prev.jawaban,
        [pertanyaanId]: value,
      },
    }));
  };

  const validateCurrentStep = () => {
    const currentPertanyaan = pertanyaan[currentStep];
    if (!currentPertanyaan) return true;

    if (
      currentPertanyaan.tipe === "nilai" &&
      !formData.jawaban[currentPertanyaan.id]
    ) {
      swal({
        title: "Perhatian",
        text: `Pertanyaan "${currentPertanyaan.pertanyaan}" harus diisi`,
        icon: "warning",
        button: "OK",
      });
      return false;
    }

    if (
      currentPertanyaan.tipe === "teks" &&
      !formData.jawaban[currentPertanyaan.id]?.trim()
    ) {
      swal({
        title: "Perhatian",
        text: `Pertanyaan "${currentPertanyaan.pertanyaan}" harus diisi`,
        icon: "warning",
        button: "OK",
      });
      return false;
    }

    return true;
  };

  const handleNext = () => {
    // Validasi pendidikan dan pekerjaan wajib diisi sebelum lanjut ke pertanyaan
    if (currentStep === -1 || currentStep === 0) {
      if (
        !formData.responden.pendidikan ||
        formData.responden.pendidikan === ""
      ) {
        swal({
          title: "Peringatan",
          text: "Pendidikan wajib diisi sebelum melanjutkan.",
          icon: "warning",
          button: "OK",
        });
        return;
      }
      if (
        !formData.responden.pekerjaan ||
        formData.responden.pekerjaan === ""
      ) {
        swal({
          title: "Peringatan",
          text: "Pekerjaan wajib diisi sebelum melanjutkan.",
          icon: "warning",
          button: "OK",
        });
        return;
      }
    }

    if (!validateCurrentStep()) {
      return;
    }

    if (currentStep < pertanyaan.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (submitting || !surveyDetail) return;

    // Validasi pendidikan dan pekerjaan wajib diisi
    if (
      !formData.responden.pendidikan ||
      formData.responden.pendidikan === ""
    ) {
      swal({
        title: "Peringatan",
        text: "Pendidikan wajib diisi sebelum mengirim survey.",
        icon: "warning",
        button: "OK",
      });
      return;
    }
    if (!formData.responden.pekerjaan || formData.responden.pekerjaan === "") {
      swal({
        title: "Peringatan",
        text: "Pekerjaan wajib diisi sebelum mengirim survey.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    if (!validateCurrentStep()) {
      return;
    }

    // Konfirmasi sebelum submit
    const confirmResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin mengirimkan survey kepuasan ini?",
      icon: "question",
      buttons: ["Batal", "Ya, Kirim"],
      dangerMode: false,
    });

    if (!confirmResult) {
      return; // User membatalkan
    }

    // Validasi semua pertanyaan
    for (const p of pertanyaan) {
      if (p.tipe === "nilai" && !formData.jawaban[p.id]) {
        swal({
          title: "Perhatian",
          text: `Pertanyaan "${p.pertanyaan}" harus diisi`,
          icon: "warning",
          button: "OK",
        });
        return;
      }
      if (p.tipe === "teks" && !formData.jawaban[p.id]?.trim()) {
        swal({
          title: "Perhatian",
          text: `Pertanyaan "${p.pertanyaan}" harus diisi`,
          icon: "warning",
          button: "OK",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Pastikan IP address sudah diambil, jika belum coba ambil lagi
      let finalIPAddress = formData.responden.ip_address;
      if (!finalIPAddress) {
        finalIPAddress = await fetchIPAddress();
      }

      // Format jawaban untuk backend
      const jawabanArray = pertanyaan.map((p) => {
        const jawaban = {
          id_pertanyaan: p.id,
        };

        if (p.tipe === "nilai") {
          jawaban.id_opsi = formData.jawaban[p.id];
        } else if (p.tipe === "teks") {
          jawaban.jawaban_teks = formData.jawaban[p.id];
        }

        return jawaban;
      });

      // Ambil id_smart dari localStorage jika ada
      const user = JSON.parse(localStorage.getItem("user"));
      const idSmart =
        user && user.uid_RSS ? user.uid_RSS : formData.responden.id_smart;

      const payload = {
        responden: {
          ...formData.responden,
          ip_address: finalIPAddress || formData.responden.ip_address,
          id_layanan: layananId ? parseInt(layananId) : null,
          id_smart: idSmart,
        },
        jawaban: jawabanArray,
      };

      await axios.post(
        `${API_ESA_OFFICE}survey-kepuasan/survey/${surveyId}/submit`,
        payload
      );

      // Pesan terima kasih
      swal({
        title: "Terima Kasih!",
        html: `
          <div style="text-align: center; padding: 20px;">
            <div style="font-size: 64px; margin-bottom: 20px;">🙏</div>
            <p style="font-size: 16px; line-height: 1.6; margin: 10px 0;">
              Terima kasih sudah mengisi form survey kepuasan pelayanan. Masukan anda sangat bermanfaat bagi kami untuk memperbaiki untuk meningkatkan pelayanan.
            </p>
          </div>
        `,
        icon: "success",
        button: "OK",
      }).then(() => {
        // Reset form
        const initialJawaban = {};
        pertanyaan.forEach((p) => {
          if (p.tipe === "nilai") {
            initialJawaban[p.id] = null;
          } else if (p.tipe === "teks") {
            initialJawaban[p.id] = "";
          }
        });
        // Reset form tapi pertahankan IP address, id_layanan, dan id_smart
        const currentIP = formData.responden.ip_address;
        const currentIdLayanan = layananId
          ? parseInt(layananId)
          : formData.responden.id_layanan || null;
        const user = JSON.parse(localStorage.getItem("user"));
        const currentIdSmart = user && user.uid_RSS ? user.uid_RSS : null;
        setFormData({
          responden: {
            nama: "",
            pendidikan: "",
            pekerjaan: "",
            id_smart: currentIdSmart, // Pertahankan id_smart dari localStorage
            ip_address: currentIP, // Pertahankan IP address
            id_layanan: currentIdLayanan, // Pertahankan id_layanan dari params
          },
          jawaban: initialJawaban,
        });
        setCurrentStep(0);
      });
    } catch (error) {
      console.error("Error submitting survey:", error);
      swal({
        title: "Error",
        text: error.response?.data?.message || "Gagal mengirim jawaban survey",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getProgress = () => {
    const totalSteps = pertanyaan.length;
    const currentProgress = currentStep + 1; // currentStep is 0-indexed
    return (currentProgress / totalSteps) * 100;
  };

  // Opsi Pendidikan
  const opsiPendidikan = [
    "Tidak Sekolah",
    "Tamat SD",
    "Tamat SMP",
    "Tamat SMA",
    "S1",
    "S2",
    "S3",
  ];

  // Opsi Pekerjaan
  const opsiPekerjaan = [
    "Petani",
    "Nelayan",
    "Buruh",
    "ASN",
    "TNI/POLRI",
    "Mahasiswa",
    "Tidak Bekerja",
  ];

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data survey...
            </MDTypography>
          </MDBox>
        </Container>
      </ThemeProvider>
    );
  }

  if (!surveyDetail) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <MDBox
            component="div"
            borderRadius="lg"
            shadow="md"
            bgColor="white"
            p={3}
            textAlign="center"
          >
            <MDTypography variant="h6" color="error">
              Survey tidak ditemukan
            </MDTypography>
            <MDTypography variant="body2" color="text" mt={1}>
              Survey dengan ID {surveyId} tidak ditemukan atau telah dihapus.
            </MDTypography>
          </MDBox>
        </Container>
      </ThemeProvider>
    );
  }

  const isLastStep = currentStep === pertanyaan.length - 1;
  const isFirstStep = currentStep === -1;
  const currentPertanyaan = currentStep >= 0 ? pertanyaan[currentStep] : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Banner Header */}
        <MDBox
          sx={{
            position: "relative",
            width: "100%",
            height: "200px",
            overflow: "hidden",
            borderRadius: "8px",
            mb: 3,
            boxShadow: 3,
          }}
        >
          <img
            src={bannerImage}
            alt="ESA Office Banner"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </MDBox>

        {/* Informasi Survey - Dipindahkan ke bawah banner */}
        <MDBox mb={3} textAlign="center">
          {dinasDetail?.nama_dinas && (
            <MDTypography variant="h6" color="text" fontWeight="medium" mb={1}>
              {dinasDetail.nama_dinas}
            </MDTypography>
          )}
          {layananDetail?.layanan && (
            <MDTypography variant="h5" color="text" fontWeight="medium" mb={1}>
              Layanan: {layananDetail.layanan}
            </MDTypography>
          )}
          <MDTypography
            variant="h4"
            color="text"
            fontWeight="bold"
            textAlign="center"
            mb={1}
          >
            {surveyDetail.judul_survey || surveyDetail.nama_survey}
          </MDTypography>
          {surveyDetail.deskripsi && (
            <MDTypography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mb={1}
            >
              {surveyDetail.deskripsi}
            </MDTypography>
          )}
        </MDBox>

        {/* Progress Bar */}
        <MDBox mb={3}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <MDTypography variant="caption" color="text">
              Progress: {currentStep + 2} / {pertanyaan.length + 1}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {Math.round(getProgress())}%
            </MDTypography>
          </MDBox>
          <MDProgress value={getProgress()} color="info" />
        </MDBox>

        {/* Main Content Card */}
        <MDBox
          component="div"
          borderRadius="lg"
          shadow="md"
          bgColor="white"
          p={4}
          minHeight="500px"
        >
          <Fade in={true} timeout={300}>
            <MDBox>
              {/* Data Responden - Ditampilkan di bagian atas, bukan sebagai step terpisah */}
              <MDBox mb={4}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Data Responden
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  mb={2}
                  display="block"
                >
                  * Nama bersifat opsional. Pendidikan dan Pekerjaan wajib
                  diisi.
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                >
                  <MDInput
                    label="Nama (Opsional)"
                    value={formData.responden.nama}
                    onChange={(e) =>
                      handleRespondenChange("nama", e.target.value)
                    }
                    fullWidth
                  />
                  <Autocomplete
                    options={opsiPendidikan}
                    value={formData.responden.pendidikan || null}
                    onChange={(event, newValue) => {
                      handleRespondenChange("pendidikan", newValue || "");
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Pendidikan *"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                    fullWidth
                  />
                  <Autocomplete
                    options={opsiPekerjaan}
                    value={formData.responden.pekerjaan || null}
                    onChange={(event, newValue) => {
                      handleRespondenChange("pekerjaan", newValue || "");
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Pekerjaan *"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                    fullWidth
                  />
                </MDBox>
              </MDBox>

              {/* Step: Pertanyaan */}
              {currentPertanyaan && (
                <MDBox>
                  <MDBox display="flex" alignItems="center" mb={3}>
                    <MDBox
                      borderRadius="50%"
                      bgColor="info"
                      color="white"
                      width="50px"
                      height="50px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={2}
                    >
                      <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        color="white"
                      >
                        {currentStep + 1}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="h5" fontWeight="medium">
                      Pertanyaan {currentStep + 1} dari {pertanyaan.length}
                    </MDTypography>
                  </MDBox>

                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2 }}>
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        color="text"
                      >
                        {currentPertanyaan.pertanyaan}
                      </MDTypography>
                    </FormLabel>

                    {currentPertanyaan.tipe === "nilai" ? (
                      <RadioGroup
                        value={formData.jawaban[currentPertanyaan.id] || ""}
                        onChange={(e) =>
                          handleJawabanChange(
                            currentPertanyaan.id,
                            parseInt(e.target.value)
                          )
                        }
                      >
                        {currentPertanyaan.opsi?.map((opsi) => (
                          <MDBox
                            key={opsi.id}
                            mb={2}
                            p={2}
                            borderRadius="lg"
                            sx={{
                              border:
                                formData.jawaban[currentPertanyaan.id] ===
                                opsi.id
                                  ? "2px solid"
                                  : "1px solid",
                              borderColor:
                                formData.jawaban[currentPertanyaan.id] ===
                                opsi.id
                                  ? "primary.main"
                                  : "grey.300",
                              bgcolor:
                                formData.jawaban[currentPertanyaan.id] ===
                                opsi.id
                                  ? "primary.lighter"
                                  : "transparent",
                              cursor: "pointer",
                              transition: "all 0.3s",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: "primary.lighter",
                              },
                            }}
                            onClick={() =>
                              handleJawabanChange(currentPertanyaan.id, opsi.id)
                            }
                          >
                            <FormControlLabel
                              value={opsi.id}
                              control={<Radio />}
                              label={
                                <MDTypography
                                  variant="body1"
                                  fontWeight="medium"
                                >
                                  {opsi.label}{" "}
                                  <span style={{ color: "#666" }}>
                                    (Nilai: {opsi.nilai})
                                  </span>
                                </MDTypography>
                              }
                              sx={{ margin: 0, width: "100%" }}
                            />
                          </MDBox>
                        ))}
                      </RadioGroup>
                    ) : (
                      <TextField
                        multiline
                        rows={6}
                        fullWidth
                        value={formData.jawaban[currentPertanyaan.id] || ""}
                        onChange={(e) =>
                          handleJawabanChange(
                            currentPertanyaan.id,
                            e.target.value
                          )
                        }
                        placeholder="Masukkan jawaban Anda di sini..."
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "1rem",
                            "&:hover fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                        }}
                      />
                    )}
                  </FormControl>
                </MDBox>
              )}

              {/* Navigation Buttons */}
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={4}
                pt={3}
                borderTop="1px solid"
                borderColor="grey.300"
              >
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={handlePrevious}
                  disabled={isFirstStep}
                  startIcon={<Icon>arrow_back</Icon>}
                >
                  Sebelumnya
                </MDButton>

                {isLastStep ? (
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleSubmit}
                    disabled={submitting}
                    endIcon={
                      submitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Icon>send</Icon>
                      )
                    }
                  >
                    {submitting ? "Mengirim..." : "Kirim Survey"}
                  </MDButton>
                ) : (
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleNext}
                    endIcon={<Icon>arrow_forward</Icon>}
                  >
                    Selanjutnya
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          </Fade>
        </MDBox>
      </Container>
    </ThemeProvider>
  );
}

export default FormSurveyPublik;
